import { Avatar } from "@ui/Avatar";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { useAppContextOptional } from "./layoutProvider";

type MenuItemLinkProps = {
	separator?: false;
	label: string;
	symbol?: string;
	avatar?: { src?: string | null; alt: string };
	rightSymbol?: string;
	/** Badge on the right (e.g. count or "Set up"). Takes precedence over rightSymbol. */
	badge?: number | string;
	url: string;
	activeMatch?: string | RegExp;
	class?: string;
	collapsed?: boolean;
};

type MenuItemSeparatorProps = {
	separator: true;
};

export type MenuItemProps = MenuItemLinkProps | MenuItemSeparatorProps;

export const MenuItem = (props: MenuItemProps) => (
	<Show
		when={!props.separator}
		fallback={
			<li class="menu-item-separator">
				<hr />
			</li>
		}
	>
		<MenuItemLink {...(props as MenuItemLinkProps)} />
	</Show>
);

const MenuItemLink = (props: MenuItemLinkProps) => {
	const layout = useAppContextOptional();
	const collapsed = () =>
		props.collapsed ?? (layout ? !layout.menuIsOpen() : false);
	const [locationTick, setLocationTick] = createSignal(0);
	onMount(() => {
		const bump = () => setLocationTick((n) => n + 1);
		window.addEventListener("hashchange", bump);
		window.addEventListener("popstate", bump);
		onCleanup(() => {
			window.removeEventListener("hashchange", bump);
			window.removeEventListener("popstate", bump);
		});
	});

	const isActive = () => {
		locationTick();
		const pathname = window.location.pathname;
		const hashIndex = props.url.indexOf("#");
		if (hashIndex >= 0) {
			const path = props.url.slice(0, hashIndex);
			const hash = props.url.slice(hashIndex);
			return pathname === path && window.location.hash === hash;
		}
		if (typeof props.activeMatch === "string") {
			return pathname.includes(props.activeMatch);
		}
		if (props.activeMatch instanceof RegExp) {
			return pathname.match(props.activeMatch) !== null;
		}
		return pathname === props.url;
	};
	return (
		<li class={props.class ?? ""}>
			<Show when={!collapsed()}>
				<Link {...props} isActive={isActive} />
			</Show>
			<Show when={collapsed()}>
				<TooltipTrigger placement="right" anchorTo=".side-bar">
					<a
						href={props.url}
						style={{ width: "2.8rem" }}
						class={isActive() ? "active" : ""}
						aria-current={isActive() ? "page" : undefined}
					>
						<LeadingIcon {...props} />
					</a>
					<Tooltip>{props.label}</Tooltip>
				</TooltipTrigger>
			</Show>
		</li>
	);
};

const LeadingIcon = (props: MenuItemLinkProps) => (
	<Show
		when={props.avatar}
		fallback={<MaterialSymbol symbol={props.symbol ?? ""} />}
	>
		{(avatar) => (
			<Avatar
				alt={avatar().alt}
				src={avatar().src ?? undefined}
				size="xsmall"
			/>
		)}
	</Show>
);

const showBadge = (badge: MenuItemLinkProps["badge"]) => {
	if (badge === undefined) {
		return false;
	}
	if (typeof badge === "number") {
		return badge > 0;
	}
	return badge.length > 0;
};

const Link = (props: MenuItemLinkProps & { isActive: () => boolean }) => {
	return (
		<a
			href={props.url}
			class={props.isActive() ? "active" : ""}
			aria-current={props.isActive() ? "page" : undefined}
		>
			<LeadingIcon {...props} />
			<span class="menu-item-label">{props.label}</span>
			{showBadge(props.badge) ? (
				<span
					class="menu-item-badge"
					classList={{
						"menu-item-badge--text": typeof props.badge === "string",
					}}
				>
					{props.badge}
				</span>
			) : props.rightSymbol ? (
				<MaterialSymbol symbol={props.rightSymbol} />
			) : null}
		</a>
	);
};
